import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { Button, Typography, ConfirmDialog } from '@occc/ui-design-system';
import { ProductGroupSelect } from '@occc-app/design-system';
import { ProductGroupFragment } from '@occc/graphql';
import CreateProductGroupFormContainer from '@occc-app/routes/ProductGroup/ProductGroupCreatePage/CreateProductGroupFormContainer';
import { ProductGroupFormRenderCallback } from '@occc-app/routes/ProductGroup/types';

import { ProductGroupSelectReadOnly } from './ProductGroupSelectReadOnly';
import { SelectedProductGroupsDetails } from './SelectedProductGroupsDetails';
import { OnSelectExistingProductGroup, OnAutoCompleteChange } from './types';

import { FormLabel } from '../../../FormLabel';
import { Column } from '../../../Column';
import { Row } from '../../../Row';

interface IProductGroupSelectEnhancedProps {
  loading: boolean;
  productGroups: ProductGroupFragment[];
  selectedProductGroupIds: string[];
  onSubmit: () => void;
  onChange: OnAutoCompleteChange;
  onSelect: OnSelectExistingProductGroup;
  renderExistingProductGroupsContainer: ProductGroupFormRenderCallback;
  editMode: boolean;
  readonly: boolean;
  label?: string;
  ineligibleType?: boolean;
  open: boolean;
  showModal: () => void;
  hideModal: () => void;
}

export const ProductGroupSelectEnhanced: React.FC<IProductGroupSelectEnhancedProps> = (props) => {
  const navigate = useNavigate();
  const {
    loading,
    productGroups,
    selectedProductGroupIds,
    onSubmit,
    onChange,
    onSelect,
    editMode,
    readonly,
    ineligibleType,
    open,
    showModal,
    hideModal,
    label,
    renderExistingProductGroupsContainer,
  } = props;

  const navigateToCreate = () => navigate('/product-groups/create');

  if (loading && !productGroups) {
    return <CircularProgress />;
  }
  if (loading && !productGroups.length) {
    return <Typography>No data</Typography>;
  }

  const selectedProductGroups = productGroups.filter((pg) =>
    selectedProductGroupIds.includes(pg.id),
  );

  if (readonly) {
    return (
      <ProductGroupSelectReadOnly groups={selectedProductGroups} ineligibleType={ineligibleType} />
    );
  }

  return (
    <Box pl='1px'>
      <Row justifyContent='space-between' alignItems='flex-start'>
        <Column width='70%'>
          <ProductGroupSelect
            onChange={onChange}
            value={selectedProductGroups}
            disabled={readonly}
            loading={loading}
            label={label}
          />
        </Column>
        <Column>
          <FormLabel>New Product Group</FormLabel>
          <Button variant='contained' onClick={!editMode ? showModal : navigateToCreate}>
            Create
          </Button>
        </Column>
      </Row>
      <SelectedProductGroupsDetails selectedProductGroups={selectedProductGroups} />
      <ConfirmDialog
        dialogTitle='Product Group'
        open={open}
        onClose={hideModal}
        confirmButtonText='Create'
        confirmButtonProps={{
          onClick: onSubmit,
        }}
      >
        <CreateProductGroupFormContainer
          onCreateCompleted={onSelect}
          renderFormChildren={renderExistingProductGroupsContainer}
        />
      </ConfirmDialog>
    </Box>
  );
};
